import React from "react";
import { Box, Link, Typography } from "@mui/material";
import warningIcon from "@assets/facilityNetwork/conflict-warning.svg";
import reviewChevron from "@assets/facilityNetwork/conflict-review-chevron.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import type { UnlinkRequestBannerData } from "./facilityNetworkUnlinkRequestUtils";
import { FN } from "./facilityNetworkTheme";

type Props = {
  request: UnlinkRequestBannerData;
  actionLabel?: "View" | "Review request" | "Review Request";
  onAction: () => void;
  /** Figma 9227:112116 / AB#10945 — distinctive red banner; copy never names P_new (C). */
  severity?: "warning" | "error";
};

/** Figma 8530:64793 / 9227:112116 — unlink request banner (B notified) */
export default function FacilityNetworkUnlinkRequestBanner({
  request,
  actionLabel = "Review Request",
  onAction,
  severity = "error",
}: Props) {
  const isError = severity === "error";
  const titleColor = isError ? "#a4262c" : "#8b4409";
  const bg = isError ? "#fde7e9" : "#fff4e5";
  const border = isError ? "#e7a3a7" : "#fabc89";

  const reason =
    request.reason?.trim() ||
    "Facility is being reassigned to another parent network";
  const subtitleParts = [
    `${request.counterpartyFacilityName} has requested to unlink`,
    `Reason: ${reason}`,
    request.expiresInDays != null
      ? `Expires in ${request.expiresInDays} day${request.expiresInDays === 1 ? "" : "s"}`
      : null,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        bgcolor: bg,
        border: `1px solid ${border}`,
        borderRadius: "4px",
        p: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: { xs: "wrap", md: "nowrap" },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
        <Box sx={{ width: 24, height: 24, flexShrink: 0, display: "flex", alignItems: "center" }}>
          <FacilityNetworkFigmaIcon src={warningIcon} size={24} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: titleColor,
              lineHeight: "20px",
            }}
          >
            Unlink request from {request.counterpartyFacilityName}
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 400,
              color: FN.figmaAccent,
              lineHeight: "20px",
            }}
          >
            {subtitleParts.join(" · ")}
          </Typography>
        </Box>
      </Box>

      <Link
        component="button"
        type="button"
        underline="none"
        onClick={() => onAction()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          flexShrink: 0,
          px: 1.5,
          py: 0.75,
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 400,
          color: titleColor,
          lineHeight: "20px",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {actionLabel === "View" ? "View" : "Review Request"}
        <FacilityNetworkFigmaIcon src={reviewChevron} size={12} />
      </Link>
    </Box>
  );
}
